import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  GraduationCap, Users, CheckCircle2, Clock, AlertTriangle,
  Sparkles, Plus, Search, User as UserIcon, KeyRound, X,
  BookOpen, ShieldCheck, CalendarClock, Pencil, FileText,
  ExternalLink, Monitor,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";

/**
 * OrgDev — Live LMS / Training Matrix module (iter48 Phase B).
 * Wires to /api/employees, /api/competencies, /api/training-matrix,
 * /api/training/sign, /api/documents.
 */

const STATUS_META = {
  certified:    { color: "#1a8754", label: "Certified",   icon: CheckCircle2 },
  expiring:     { color: "#d6a900", label: "Expiring",    icon: Clock },
  expired:      { color: "#ff4500", label: "Expired",     icon: AlertTriangle },
  required:     { color: "#b93a1a", label: "Required",    icon: AlertTriangle },
  optional:     { color: "#1a1a1a55", label: "Optional",  icon: null },
  na:           { color: "#1a1a1a10", label: "N/A",       icon: null },
};

// ─── helpers ──────────────────────────────────────────────────────────
function daysBetween(a, b) {
  return Math.round((new Date(a) - new Date(b)) / (1000 * 60 * 60 * 24));
}

function cellStatus(cell, comp) {
  if (!cell || cell.requirement === "na") return "na";
  if (!cell.last_signed_at) return cell.requirement === "required" ? "required" : "optional";
  const rd = comp?.refresh_days;
  if (!rd || !cell.expires_at) return "certified";
  const daysLeft = daysBetween(cell.expires_at, new Date().toISOString());
  if (daysLeft < 0) return "expired";
  if (daysLeft < 30) return "expiring";
  return "certified";
}

// ─── Roster tab ───────────────────────────────────────────────────────
function RosterTab({ employees, canEdit, reload, matrix, competencies }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [profile, setProfile] = useState(null);
  const [search, setSearch] = useState("");

  const filtered = employees.filter((e) =>
    !search || (e.name + " " + (e.position || "")).toLowerCase().includes(search.toLowerCase())
  );

  const empStats = (empId) => {
    const cells = (matrix?.cells || []).filter((c) => c.employee_id === empId);
    let certified = 0, expiring = 0, expired = 0, required = 0;
    cells.forEach((c) => {
      const comp = competencies.find((k) => k.id === c.competency_id);
      const st = cellStatus(c, comp);
      if (st === "certified") certified++;
      else if (st === "expiring") expiring++;
      else if (st === "expired") expired++;
      else if (st === "required") required++;
    });
    return { certified, expiring, expired, required };
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1a1a1a]/40" />
          <input
            data-testid="orgdev-roster-search"
            className="pl-9 pr-3 py-2 rounded-lg border border-[#1a1a1a]/15 bg-white text-sm w-64"
            placeholder="Search employees…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canEdit && (
          <button
            data-testid="orgdev-add-employee"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#ff4500] text-white text-xs font-bold uppercase tracking-wider"
          >
            <Plus className="w-4 h-4" /> Add Employee
          </button>
        )}
      </div>

      <div className="surface-card rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1a1a1a] text-white">
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Name (last, first)</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Position</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Start date</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-bold">Certified</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-bold">Expiring</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-bold">Expired</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-bold">Gaps</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]/06">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#1a1a1a]/50">No employees. Add one to get started.</td></tr>
            )}
            {filtered.map((e) => {
              const s = empStats(e.id);
              const displayName = (e.last_name || e.first_name)
                ? `${e.last_name || ""}${e.last_name && e.first_name ? ", " : ""}${e.first_name || ""}`
                : e.name;
              return (
                <tr key={e.id} data-testid={`orgdev-roster-${e.id}`} className="hover:bg-[#f7f5f2]">
                  <td className="px-4 py-2.5">
                    <button
                      data-testid={`orgdev-roster-open-${e.id}`}
                      onClick={() => setProfile(e)}
                      className="font-display font-bold text-[13px] text-[#1a1a1a] hover:text-[#ff4500] text-left"
                    >
                      {displayName}
                    </button>
                    {e.employee_id_ext && <div className="text-[10px] font-mono text-[#1a1a1a]/50">#{e.employee_id_ext}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-[#1a1a1a]/70">{e.position || "—"}</td>
                  <td className="px-4 py-2.5 text-[11px] font-mono text-[#1a1a1a]/70">{e.start_date ? e.start_date.slice(0, 10) : "—"}</td>
                  <td className="px-3 py-2.5 text-center font-mono font-bold text-[#1a8754]">{s.certified}</td>
                  <td className="px-3 py-2.5 text-center font-mono font-bold text-[#d6a900]">{s.expiring}</td>
                  <td className="px-3 py-2.5 text-center font-mono font-bold text-[#ff4500]">{s.expired}</td>
                  <td className="px-3 py-2.5 text-center font-mono font-bold text-[#b93a1a]">{s.required}</td>
                  <td className="px-3 py-2.5 text-right">
                    {canEdit && (
                      <button
                        data-testid={`orgdev-roster-edit-${e.id}`}
                        onClick={() => { setEditing(e); setShowForm(true); }}
                        className="p-1.5 rounded hover:bg-[#1a1a1a]/05 text-[#1a1a1a]/60"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <EmployeeFormDialog
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload(); }}
        />
      )}

      {profile && (
        <EmployeeProfileDialog
          employee={profile}
          matrix={matrix}
          competencies={competencies}
          canEdit={canEdit}
          onClose={() => setProfile(null)}
          onSigned={reload}
        />
      )}
    </>
  );
}

function EmployeeFormDialog({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    last_name: initial?.last_name || "",
    first_name: initial?.first_name || "",
    position: initial?.position || "",
    employee_id_ext: initial?.employee_id_ext || "",
    start_date: initial?.start_date || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    pin: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.last_name.trim() || !form.first_name.trim()) return toast.error("Last name and first name are required");
    if (form.pin && !/^\d{4,8}$/.test(form.pin)) return toast.error("PIN must be 4–8 digits");
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.pin) delete body.pin;
      if (!body.start_date) delete body.start_date;
      if (isEdit) {
        await api.patch(`/employees/${initial.id}`, body);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", body);
        toast.success("Employee added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="orgdev-employee-form">
        <DialogHeader><DialogTitle>{isEdit ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Last name *</label>
              <input data-testid="orgdev-form-last-name" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">First name *</label>
              <input data-testid="orgdev-form-first-name" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Position</label>
              <input data-testid="orgdev-form-position" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Emp. ID</label>
              <input data-testid="orgdev-form-emp-id" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.employee_id_ext} onChange={(e) => setForm({ ...form, employee_id_ext: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Start date</label>
            <input data-testid="orgdev-form-start-date" type="date" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Email (optional)</label>
              <input className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Phone (optional)</label>
              <input className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
              Kiosk PIN {isEdit && "(leave blank to keep current)"}
            </label>
            <input
              data-testid="orgdev-form-pin"
              type="password"
              maxLength={8}
              placeholder="4–8 digit numeric"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15 font-mono"
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
            />
            <div className="text-[10px] text-[#1a1a1a]/50 mt-1">Employees use this PIN at kiosk-mode SOP sign-off.</div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/60">Cancel</button>
          <button data-testid="orgdev-form-save" disabled={busy} onClick={save} className="px-3 py-1.5 rounded-lg bg-[#1a1a1a] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40">
            {isEdit ? "Save" : "Add"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Employee Profile (click-through) ─────────────────────────────────
function EmployeeProfileDialog({ employee, matrix, competencies, canEdit, onClose, onSigned }) {
  const cells = (matrix?.cells || []).filter((c) => c.employee_id === employee.id);
  const [signOpen, setSignOpen] = useState(null); // competency object

  const rows = competencies.map((comp) => {
    const cell = cells.find((c) => c.competency_id === comp.id);
    const st = cellStatus(cell, comp);
    return { comp, cell, st };
  });

  const stats = {
    certified: rows.filter((r) => r.st === "certified").length,
    expiring:  rows.filter((r) => r.st === "expiring").length,
    expired:   rows.filter((r) => r.st === "expired").length,
    required:  rows.filter((r) => r.st === "required").length,
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="orgdev-profile">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-[#ff4500]" />
            <span className="font-display">{employee.name}</span>
            <span className="text-xs font-mono uppercase tracking-wider text-[#1a1a1a]/50">· {employee.position || "—"}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-2 my-2">
          {["certified", "expiring", "expired", "required"].map((k) => (
            <div key={k} className="rounded-lg border border-[#1a1a1a]/10 p-2 text-center">
              <div className="font-display font-black text-xl" style={{ color: STATUS_META[k].color }}>{stats[k]}</div>
              <div className="text-[9px] uppercase tracking-wider font-mono text-[#1a1a1a]/60">{STATUS_META[k].label}</div>
            </div>
          ))}
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-[#1a1a1a]/10">
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-mono text-[#1a1a1a]/60">Competency</th>
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-mono text-[#1a1a1a]/60">Status</th>
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-mono text-[#1a1a1a]/60">Last signed</th>
                <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-mono text-[#1a1a1a]/60">Expires</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]/06">
              {rows.map(({ comp, cell, st }) => {
                const meta = STATUS_META[st];
                const Icon = meta.icon;
                return (
                  <tr key={comp.id} data-testid={`orgdev-profile-row-${comp.id}`}>
                    <td className="px-2 py-2">
                      <div className="font-display font-bold text-[13px]">{comp.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">{comp.category}</div>
                    </td>
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: `${meta.color}20`, color: meta.color }}>
                        {Icon && <Icon className="w-3 h-3" />} {meta.label}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-[11px] font-mono text-[#1a1a1a]/70">{cell?.last_signed_at?.slice(0, 10) || "—"}</td>
                    <td className="px-2 py-2 text-[11px] font-mono text-[#1a1a1a]/70">
                      {cell?.expires_at ? cell.expires_at.slice(0, 10) : (comp.refresh_days ? "—" : "no expiry")}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {canEdit && (comp.taught_by_document_ids || []).length > 0 && (
                        <button
                          data-testid={`orgdev-profile-sign-${comp.id}`}
                          onClick={() => setSignOpen(comp)}
                          className="px-2 py-1 rounded bg-[#1a1a1a] text-white text-[10px] font-bold uppercase tracking-wider"
                        >
                          Sign
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#1a1a1a]/50">No competencies defined yet — add competencies in the Training Catalog tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {signOpen && (
          <ReadSignDialog
            employee={employee}
            competency={signOpen}
            onClose={() => setSignOpen(null)}
            onSigned={() => { setSignOpen(null); onSigned(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Read+Sign Dialog ─────────────────────────────────────────────────
function ReadSignDialog({ employee, competency, onClose, onSigned }) {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState((competency.taught_by_document_ids || [])[0] || "");
  const [mode, setMode] = useState("supervisor_witnessed"); // or self_kiosk
  const [pin, setPin] = useState("");
  const [supervisorNote, setSupervisorNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/documents");
        const linked = (r.data || []).filter((d) => (competency.taught_by_document_ids || []).includes(d.id));
        setDocs(linked);
      } catch (e) { /* ignore */ }
    })();
  }, [competency]);

  const doc = docs.find((d) => d.id === docId);

  const submit = async () => {
    if (!doc) return toast.error("Pick a document");
    if (!doc.current_rev) return toast.error("Document has no approved revision yet");
    if (mode === "self_kiosk" && !/^\d{4,8}$/.test(pin)) return toast.error("Employee PIN required");
    setBusy(true);
    try {
      await api.post("/training/sign", {
        employee_id: employee.id,
        competency_id: competency.id,
        document_id: doc.id,
        document_rev: doc.current_rev,
        signature_kind: mode,
        pin: mode === "self_kiosk" ? pin : undefined,
        supervisor_note: mode === "supervisor_witnessed" ? supervisorNote : undefined,
      });
      toast.success("Training signed");
      onSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="orgdev-sign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#1a8754]" />
            Read + Sign · {competency.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="text-[12px] text-[#1a1a1a]/70">Signing for <b>{employee.name}</b> · {employee.position || "—"}</div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Document / SOP</label>
            <select data-testid="orgdev-sign-doc" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={docId} onChange={(e) => setDocId(e.target.value)}>
              {docs.map((d) => {
                const label = `${d.code} · ${d.title} · rev ${d.current_rev || "draft"}`;
                return <option key={d.id} value={d.id}>{label}</option>;
              })}
            </select>
          </div>

          <div className="flex gap-2">
            {[
              ["supervisor_witnessed", "Supervisor witnessed"],
              ["self_kiosk", "Kiosk (PIN)"],
            ].map(([k, l]) => (
              <button key={k} data-testid={`orgdev-sign-mode-${k}`} onClick={() => setMode(k)} className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border ${mode === k ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a]/70 border-[#1a1a1a]/15"}`}>{l}</button>
            ))}
          </div>

          {mode === "supervisor_witnessed" && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Supervisor note (optional)</label>
              <textarea data-testid="orgdev-sign-note" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" rows={3} placeholder="Read, understood, and demonstrated…" value={supervisorNote} onChange={(e) => setSupervisorNote(e.target.value)} />
            </div>
          )}

          {mode === "self_kiosk" && (
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Employee&apos;s kiosk PIN</label>
              <input data-testid="orgdev-sign-pin" type="password" maxLength={8} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15 font-mono" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/60">Cancel</button>
          <button data-testid="orgdev-sign-submit" disabled={busy} onClick={submit} className="px-3 py-1.5 rounded-lg bg-[#1a8754] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40">
            Record signature
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Matrix (heatmap) tab ─────────────────────────────────────────────
function MatrixTab({ matrix, competencies, canEdit, reload }) {
  const employees = matrix?.employees || [];
  const cells = matrix?.cells || [];

  const cellFor = (empId, compId) => cells.find((c) => c.employee_id === empId && c.competency_id === compId);

  const cycle = async (empId, comp) => {
    if (!canEdit) return;
    const c = cellFor(empId, comp.id);
    const current = c?.requirement || "na";
    const next = current === "na" ? "required" : current === "required" ? "optional" : "na";
    try {
      await api.put("/training-matrix/cell", { employee_id: empId, competency_id: comp.id, requirement: next });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (competencies.length === 0) {
    return <div className="surface-card rounded-2xl p-8 text-center text-sm text-[#1a1a1a]/60">No competencies defined. Go to Training Catalog tab to create your first competency.</div>;
  }
  if (employees.length === 0) {
    return <div className="surface-card rounded-2xl p-8 text-center text-sm text-[#1a1a1a]/60">No employees on the roster yet.</div>;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-wider">
        {["certified", "expiring", "expired", "required", "optional", "na"].map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded" style={{ background: STATUS_META[k].color, opacity: k === "na" ? 0.5 : 1 }} />
            {STATUS_META[k].label}
          </div>
        ))}
        {canEdit && <div className="text-[#1a1a1a]/50 italic normal-case">· click a cell to cycle N/A → Required → Optional</div>}
      </div>
      <div className="surface-card rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[#1a1a1a] text-white">
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold sticky left-0 bg-[#1a1a1a] z-10 min-w-[220px]">Employee</th>
              {competencies.map((c) => (
                <th key={c.id} className="px-2 py-2 text-[9px] uppercase tracking-wider font-bold text-center min-w-[100px]" title={c.description}>
                  {c.name}
                  {c.refresh_days && <div className="text-[8px] normal-case font-mono text-white/50">{c.refresh_days}d cycle</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]/06">
            {employees.map((e) => (
              <tr key={e.id} data-testid={`orgdev-matrix-row-${e.id}`} className="hover:bg-[#f7f5f2]">
                <td className="px-4 py-2.5 sticky left-0 bg-white z-10">
                  <div className="font-display font-bold text-[13px]">{e.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{e.position || "—"}</div>
                </td>
                {competencies.map((comp) => {
                  const c = cellFor(e.id, comp.id);
                  const st = cellStatus(c, comp);
                  const meta = STATUS_META[st];
                  const Icon = meta.icon;
                  return (
                    <td key={comp.id} className="px-2 py-2.5 text-center">
                      <button
                        data-testid={`orgdev-matrix-cell-${e.id}-${comp.id}`}
                        onClick={() => cycle(e.id, comp)}
                        disabled={!canEdit}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-transform hover:scale-110 disabled:cursor-default"
                        style={{ background: `${meta.color}${st === "na" ? "10" : "25"}`, color: meta.color }}
                        title={`${meta.label}${c?.last_signed_at ? " · " + c.last_signed_at.slice(0, 10) : ""}`}
                      >
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Training Catalog tab (competencies + linked docs) ────────────────
function CatalogTab({ competencies, canEdit, reload, documents, employees }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [playSession, setPlaySession] = useState(null); // competency being run

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="text-[12px] text-[#1a1a1a]/70 max-w-3xl">
          <b>Competencies</b> are the specialized trainings, certifications and role-qualifications your team needs — for example <i>Forklift Operator</i>, <i>HACCP</i>, <i>SQF Practitioner</i>, <i>SQF Internal Auditor</i>, <i>First Aid</i>, <i>AED</i>, <i>Fire Extinguisher</i>, <i>Crisis Management</i>, or role-specific ones like <i>Press Operator</i>, <i>Extrusion Operator</i>. Attach the training material (PowerPoint, PDF, video) or link (YouTube, external site). At training time, open the Training Catalog on the big screen, click the competency, and every attendee&apos;s signature is captured automatically.
        </div>
        {canEdit && (
          <button
            data-testid="orgdev-add-competency"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#ff4500] text-white text-xs font-bold uppercase tracking-wider"
          >
            <Plus className="w-4 h-4" /> Add Competency
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {competencies.map((c) => {
          const linked = documents.filter((d) => (c.taught_by_document_ids || []).includes(d.id));
          return (
            <div key={c.id} data-testid={`orgdev-competency-${c.id}`} className="surface-card rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#ff4500]">
                  <BookOpen className="w-4 h-4" />
                  <span className="font-mono text-[10px] uppercase tracking-wider font-bold">{c.category || "General"}</span>
                </div>
                {canEdit && (
                  <button data-testid={`orgdev-competency-edit-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }} className="p-1 text-[#1a1a1a]/50 hover:text-[#1a1a1a]"><Pencil className="w-3.5 h-3.5" /></button>
                )}
              </div>
              <div className="font-display font-black text-base mt-2 leading-snug">{c.name}</div>
              {c.description && <div className="text-[12px] text-[#1a1a1a]/70 mt-1">{c.description}</div>}
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mt-3">
                Refresh cycle: {c.refresh_days ? `${c.refresh_days} days` : "no expiry"}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mt-2">Linked SOPs · {linked.length}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {linked.map((d) => (
                  <span key={d.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1a1a1a]/05 text-[10px] font-mono">
                    <FileText className="w-3 h-3" /> {d.code}
                  </span>
                ))}
                {linked.length === 0 && <span className="text-[10px] text-[#1a1a1a]/40 italic">no documents linked</span>}
              </div>
              {/* iter51 — training material preview + Play Training button */}
              {(c.training_material_file_id || c.training_material_url) && (
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mt-3">
                  Material: {c.training_material_url ? "external link" : "uploaded file"}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid={`orgdev-competency-play-${c.id}`}
                  onClick={() => setPlaySession(c)}
                  disabled={!c.training_material_file_id && !c.training_material_url}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-[#1a1a1a] text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed"
                  title={c.training_material_file_id || c.training_material_url ? "Open on big screen — capture every attendee's signature" : "Attach training material first"}
                >
                  <ShieldCheck className="w-3 h-3" /> Play Training
                </button>
              </div>
            </div>
          );
        })}
        {competencies.length === 0 && (
          <div className="col-span-full surface-card rounded-2xl p-8 text-center text-sm text-[#1a1a1a]/60">
            No competencies yet. {canEdit && "Add your first competency to build the training matrix."}
          </div>
        )}
      </div>

      {showForm && (
        <CompetencyFormDialog
          initial={editing}
          documents={documents}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload(); }}
        />
      )}
      {playSession && (
        <TrainingSessionDialog
          competency={playSession}
          employees={employees}
          canEdit={canEdit}
          onClose={() => setPlaySession(null)}
          onSigned={() => { setPlaySession(null); reload(); }}
        />
      )}
    </>
  );
}

function CompetencyFormDialog({ initial, documents, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    category: initial?.category || "General",
    taught_by_document_ids: initial?.taught_by_document_ids || [],
    refresh_days: initial?.refresh_days || "",
    // iter51 — training material for classroom-training sessions
    training_material_file_id: initial?.training_material_file_id || "",
    training_material_url: initial?.training_material_url || "",
  });
  const [materialFileIds, setMaterialFileIds] = useState(initial?.training_material_file_id ? [initial.training_material_file_id] : []);
  const [busy, setBusy] = useState(false);

  const toggleDoc = (id) => {
    setForm((f) => ({
      ...f,
      taught_by_document_ids: f.taught_by_document_ids.includes(id)
        ? f.taught_by_document_ids.filter((x) => x !== id)
        : [...f.taught_by_document_ids, id],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description,
        category: form.category,
        taught_by_document_ids: form.taught_by_document_ids,
        refresh_days: form.refresh_days ? parseInt(form.refresh_days, 10) : null,
        training_material_file_id: materialFileIds[0] || null,
        training_material_url: (form.training_material_url || "").trim() || null,
      };
      if (isEdit) {
        await api.patch(`/competencies/${initial.id}`, body);
      } else {
        await api.post("/competencies", body);
      }
      toast.success("Competency saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="orgdev-competency-form">
        <DialogHeader><DialogTitle>{isEdit ? "Edit competency" : "New competency"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Name *</label>
              <input data-testid="orgdev-comp-name" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Category</label>
              <input className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Description</label>
            <textarea rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Refresh cycle (days) — blank = no expiry</label>
            <input data-testid="orgdev-comp-refresh" type="number" min="0" className="w-40 mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15" value={form.refresh_days} onChange={(e) => setForm({ ...form, refresh_days: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Taught by (link SOPs / documents)</label>
            <div className="mt-1 max-h-60 overflow-y-auto border border-[#1a1a1a]/10 rounded-lg divide-y">
              {documents.length === 0 && <div className="text-[12px] text-[#1a1a1a]/50 p-3">No documents in the library yet — head to SOP&apos;s &amp; Doc&apos;s to add one.</div>}
              {documents.map((d) => (
                <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-[#f7f5f2] cursor-pointer">
                  <input
                    data-testid={`orgdev-comp-doc-${d.id}`}
                    type="checkbox"
                    checked={form.taught_by_document_ids.includes(d.id)}
                    onChange={() => toggleDoc(d.id)}
                  />
                  <span className="font-mono text-[10px] text-[#ff4500] font-bold">{d.code}</span>
                  <span className="text-[13px]">{d.title}</span>
                  {d.current_rev && <span className="font-mono text-[10px] text-[#1a1a1a]/50">rev {d.current_rev}</span>}
                </label>
              ))}
            </div>
          </div>

          {/* iter51 — Training Material (played at classroom-training time) */}
          <div className="border-t border-[#1a1a1a]/08 pt-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold mb-2">// Training material (shown on big screen)</div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
              Upload file (PowerPoint, PDF, video, image — any type)
            </label>
            <div className="mt-1">
              <PhotoUploader
                photoIds={materialFileIds}
                onChange={setMaterialFileIds}
                testIdPrefix="comp-material-file"
                accept="*/*"
                hideCamera
              />
            </div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 mt-3 block">
              …or external URL (YouTube, vendor training portal, etc.)
            </label>
            <input
              data-testid="orgdev-comp-material-url"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15"
              placeholder="https://youtube.com/watch?v=…"
              value={form.training_material_url}
              onChange={(e) => setForm({ ...form, training_material_url: e.target.value })}
            />
            <div className="text-[10px] text-[#1a1a1a]/50 mt-1">
              You can attach either a file OR a URL (or both). At training time, Play Training will open the material and capture every attendee&apos;s signature automatically.
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/60">Cancel</button>
          <button data-testid="orgdev-comp-save" disabled={busy} onClick={save} className="px-3 py-1.5 rounded-lg bg-[#1a1a1a] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Training Session Dialog (iter51) ─────────────────────────────────
// Opens the competency's training material on the big screen and captures
// every attendee's signature via one API call.
function TrainingSessionDialog({ competency, employees, canEdit, onClose, onSigned }) {
  const [mode, setMode] = useState("supervisor_witnessed"); // or self_kiosk_batch
  const [selectedIds, setSelectedIds] = useState([]);
  const [pins, setPins] = useState({}); // { emp_id: pin }
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("opscmd_token") || "";
  const fileUrl = competency.training_material_file_id
    ? `${backend}/api/files/${competency.training_material_file_id}?auth=${encodeURIComponent(token)}`
    : null;
  const url = competency.training_material_url || null;

  // Detect if the URL is embeddable (YouTube/Vimeo) — otherwise open in new tab
  const isYouTube = url && /(youtube\.com|youtu\.be)/.test(url);
  const isVimeo = url && /vimeo\.com/.test(url);
  const embedUrl = (() => {
    if (!url) return null;
    if (isYouTube) {
      const m = url.match(/(?:v=|youtu\.be\/)([\w-]{11})/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    }
    if (isVimeo) {
      const m = url.match(/vimeo\.com\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
    return null;
  })();

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const label = `${e.last_name || ""} ${e.first_name || ""} ${e.name || ""} ${e.position || ""}`.toLowerCase();
    return label.includes(search.toLowerCase());
  });

  const toggle = (id) => {
    setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };
  const selectAll = () => setSelectedIds(filtered.map((e) => e.id));
  const selectNone = () => setSelectedIds([]);

  const submit = async () => {
    if (selectedIds.length === 0) return toast.error("Pick at least one attendee");
    if (mode === "self_kiosk_batch") {
      const missing = selectedIds.filter((id) => !/^\d{4,8}$/.test(pins[id] || ""));
      if (missing.length) return toast.error(`${missing.length} attendee(s) missing valid PIN`);
    }
    setBusy(true);
    try {
      const body = {
        employee_ids: selectedIds,
        signature_kind: mode,
        session_notes: note || null,
      };
      if (mode === "self_kiosk_batch") {
        body.pins = Object.fromEntries(selectedIds.map((id) => [id, pins[id]]));
      }
      const r = await api.post(`/competencies/${competency.id}/broadcast-sign`, body);
      const { signed_count, skipped_count, skipped } = r.data;
      if (skipped_count > 0) {
        toast.warning(`${signed_count} signed · ${skipped_count} skipped (${(skipped || []).map((s) => s.reason).join(", ")})`);
      } else {
        toast.success(`${signed_count} attendee(s) signed for "${competency.name}"`);
      }
      onSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="orgdev-training-session">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-[#ff4500]" />
            <span className="font-display font-black">Training Session · {competency.name}</span>
            {competency.refresh_days && (
              <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                Cert good for {competency.refresh_days} days
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 overflow-y-auto pr-1">
          {/* Left — material viewer */}
          <div className="bg-[#0e0e12] rounded-2xl overflow-hidden min-h-[300px] flex items-center justify-center relative">
            {embedUrl ? (
              <iframe title="training-material" data-testid="orgdev-session-embed"
                src={embedUrl} className="w-full h-[420px] rounded-2xl" allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              />
            ) : fileUrl ? (
              <iframe title="training-file" data-testid="orgdev-session-file"
                src={fileUrl} className="w-full h-[420px] bg-white rounded-2xl" />
            ) : url ? (
              <a data-testid="orgdev-session-openlink" href={url} target="_blank" rel="noreferrer"
                className="text-white p-6 text-center hover:underline">
                <ExternalLink className="w-8 h-8 mx-auto mb-2" />
                Open training material in new tab
                <div className="text-[10px] font-mono text-white/50 mt-1">{url}</div>
              </a>
            ) : (
              <div className="text-white/60 p-6 text-center text-sm">No training material attached yet.</div>
            )}
            {(fileUrl || url) && (
              <a data-testid="orgdev-session-fullscreen" href={fileUrl || url} target="_blank" rel="noreferrer"
                className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-white flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Open full-screen
              </a>
            )}
          </div>

          {/* Right — attendee capture */}
          <div className="space-y-3">
            <div className="text-[11px] text-[#1a1a1a]/70">
              Every attendee&apos;s signature will be captured and their <b>Competency Matrix + Training Matrix</b> will update automatically.
            </div>
            <div className="flex gap-1 bg-[#f7f5f2] rounded-full p-1 w-fit">
              <button data-testid="orgdev-session-mode-supervisor" onClick={() => setMode("supervisor_witnessed")}
                className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${mode === "supervisor_witnessed" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60"}`}>
                Supervisor witnessed
              </button>
              <button data-testid="orgdev-session-mode-kiosk" onClick={() => setMode("self_kiosk_batch")}
                className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${mode === "self_kiosk_batch" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60"}`}>
                Kiosk PINs
              </button>
            </div>

            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#1a1a1a]/40" />
              <input data-testid="orgdev-session-search"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#1a1a1a]/15 text-sm"
                placeholder="Search attendees…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
              <button onClick={selectAll} className="text-[#ff4500] font-bold hover:underline" data-testid="orgdev-session-select-all">Select all shown ({filtered.length})</button>
              <span className="text-[#1a1a1a]/30">·</span>
              <button onClick={selectNone} className="text-[#1a1a1a]/60 hover:underline">Clear</button>
              <span className="ml-auto text-[#1a1a1a]/60">{selectedIds.length} selected</span>
            </div>

            <div className="max-h-[240px] overflow-y-auto border border-[#1a1a1a]/10 rounded-lg divide-y">
              {filtered.length === 0 && <div className="p-3 text-[12px] text-[#1a1a1a]/50">No employees.</div>}
              {filtered.map((e) => {
                const on = selectedIds.includes(e.id);
                const display = (e.last_name || e.first_name)
                  ? `${e.last_name || ""}${e.last_name && e.first_name ? ", " : ""}${e.first_name || ""}`
                  : e.name;
                return (
                  <div key={e.id} className={`flex items-center gap-2 p-2 hover:bg-[#f7f5f2] ${on ? "bg-[#ff4500]/06" : ""}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(e.id)} data-testid={`orgdev-session-emp-${e.id}`} />
                    <span className="text-[12px] flex-1">{display}</span>
                    {e.position && <span className="text-[10px] text-[#1a1a1a]/50 font-mono">{e.position}</span>}
                    {mode === "self_kiosk_batch" && on && (
                      <input
                        type="password" maxLength={8} placeholder="PIN"
                        className="w-16 px-2 py-1 rounded border border-[#1a1a1a]/15 text-xs font-mono"
                        value={pins[e.id] || ""}
                        onChange={(ev) => setPins({ ...pins, [e.id]: ev.target.value.replace(/\D/g, "") })}
                        data-testid={`orgdev-session-pin-${e.id}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">Session notes (what was covered / room)</label>
              <textarea data-testid="orgdev-session-notes"
                rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#1a1a1a]/15"
                placeholder="e.g. Crisis Management module 1 — evacuation drill review, break-room, 8/12/2025 09:00"
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/60">Cancel</button>
              <button
                data-testid="orgdev-session-submit"
                disabled={busy || !canEdit && mode === "supervisor_witnessed"}
                onClick={submit}
                className="px-3 py-1.5 rounded-lg bg-[#1a8754] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Capture signatures ({selectedIds.length})
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Upcoming Training tab ────────────────────────────────────────────
function UpcomingTab({ matrix, competencies, canEdit, reload }) {
  const employees = matrix?.employees || [];
  const cells = matrix?.cells || [];
  const now = new Date();
  const soon = new Date(now.getTime() + 30 * 86400000);

  const rows = [];
  cells.forEach((c) => {
    if (c.requirement !== "required") return;
    const comp = competencies.find((k) => k.id === c.competency_id);
    if (!comp) return;
    const emp = employees.find((e) => e.id === c.employee_id);
    if (!emp) return;
    const st = cellStatus(c, comp);
    if (["expired", "expiring", "required"].includes(st)) {
      rows.push({ emp, comp, cell: c, st });
    }
  });
  // Also gather "required + never signed" from matrix
  employees.forEach((e) => {
    competencies.forEach((comp) => {
      const c = cells.find((x) => x.employee_id === e.id && x.competency_id === comp.id);
      if (c?.requirement === "required" && !c.last_signed_at && !rows.some((r) => r.emp.id === e.id && r.comp.id === comp.id)) {
        rows.push({ emp: e, comp, cell: c, st: "required" });
      }
    });
  });

  const priority = { expired: 0, required: 1, expiring: 2 };
  rows.sort((a, b) => (priority[a.st] ?? 9) - (priority[b.st] ?? 9));

  const [signOpen, setSignOpen] = useState(null); // {emp, comp}

  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        {["expired", "required", "expiring"].map((k) => {
          const count = rows.filter((r) => r.st === k).length;
          const meta = STATUS_META[k];
          return (
            <div key={k} className="surface-card rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
                {meta.icon && <meta.icon className="w-3.5 h-3.5" />} {meta.label}
              </div>
              <div className="font-display font-black text-3xl mt-1" style={{ color: meta.color }}>{count}</div>
            </div>
          );
        })}
      </div>

      <div className="surface-card rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1a1a1a] text-white">
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Priority</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Employee</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Competency</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Last signed</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Expires / Due</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]/06">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[#1a1a1a]/50">
                <CheckCircle2 className="w-6 h-6 inline text-[#1a8754] mr-1" /> All required training up to date — nice work.
              </td></tr>
            )}
            {rows.map(({ emp, comp, cell, st }) => {
              const meta = STATUS_META[st];
              const Icon = meta.icon;
              return (
                <tr key={`${emp.id}-${comp.id}`} data-testid={`orgdev-upcoming-${emp.id}-${comp.id}`} className="hover:bg-[#f7f5f2]">
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: `${meta.color}20`, color: meta.color }}>
                      {Icon && <Icon className="w-3 h-3" />} {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-display font-bold text-[13px]">{emp.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{emp.position || "—"}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-display font-bold text-[13px]">{comp.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{comp.category}</div>
                  </td>
                  <td className="px-4 py-2.5 text-[11px] font-mono text-[#1a1a1a]/70">{cell.last_signed_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-2.5 text-[11px] font-mono text-[#1a1a1a]/70">{cell.expires_at?.slice(0, 10) || (st === "required" ? "asap" : "—")}</td>
                  <td className="px-4 py-2.5 text-right">
                    {canEdit && (comp.taught_by_document_ids || []).length > 0 && (
                      <button
                        data-testid={`orgdev-upcoming-sign-${emp.id}-${comp.id}`}
                        onClick={() => setSignOpen({ emp, comp })}
                        className="px-2 py-1 rounded bg-[#1a8754] text-white text-[10px] font-bold uppercase tracking-wider"
                      >
                        Sign
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {signOpen && (
        <ReadSignDialog
          employee={signOpen.emp}
          competency={signOpen.comp}
          onClose={() => setSignOpen(null)}
          onSigned={() => { setSignOpen(null); reload(); }}
        />
      )}
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function OrgDev() {
  const [tab, setTab] = useState("matrix");
  const [employees, setEmployees] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [matrix, setMatrix] = useState({ employees: [], competencies: [], cells: [] });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("opscmd_user") || "{}"); } catch { return {}; }
  }, []);
  const canEdit = ["admin", "controller", "master"].includes(user?.org_role);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [emps, comps, mat, docs] = await Promise.all([
        api.get("/employees"),
        api.get("/competencies"),
        api.get("/training-matrix"),
        api.get("/documents"),
      ]);
      setEmployees(emps.data || []);
      setCompetencies(comps.data || []);
      setMatrix(mat.data || { employees: [], competencies: [], cells: [] });
      setDocuments(docs.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Org-Dev data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Aggregate stats for hero
  const stats = useMemo(() => {
    const cells = matrix?.cells || [];
    let certified = 0, expiring = 0, expired = 0, required = 0;
    cells.forEach((c) => {
      const comp = competencies.find((k) => k.id === c.competency_id);
      const st = cellStatus(c, comp);
      if (st === "certified") certified++;
      else if (st === "expiring") expiring++;
      else if (st === "expired") expired++;
      else if (st === "required") required++;
    });
    const totalRequired = certified + expiring + expired + required;
    const readiness = totalRequired > 0 ? Math.round((certified / totalRequired) * 100) : 0;
    return { certified, expiring, expired, required, readiness };
  }, [matrix, competencies]);

  return (
    <div className="p-4 lg:p-8 space-y-5" data-testid="orgdev-page">
      {/* Hero */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-16 -right-8 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <GraduationCap className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Org-Dev · Training · Competency</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">Org-Dev.</h1>
            <p className="mt-2 text-sm text-white/75 max-w-2xl">
              A complete roster of every employee — from front-line operators to leadership — with the specialized trainings and certifications each role requires. Kiosk PIN sign-off, supervisor-witnessed Read+Sign, and live decay-clocks for every cert.
            </p>
          </div>
          <div className="flex items-center gap-4 text-white">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/60">Team readiness</div>
              <div className="font-display font-black text-3xl text-[#ff4500]" data-testid="orgdev-readiness">{stats.readiness}%</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/60">Expiring 30d</div>
              <div className="font-display font-black text-3xl">{stats.expiring}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/60">Expired</div>
              <div className="font-display font-black text-3xl text-[#ff4500]">{stats.expired}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/60">Gaps</div>
              <div className="font-display font-black text-3xl">{stats.required}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="flex border border-[#1a1a1a]/20 bg-white rounded-full p-1 w-fit flex-wrap">
        {[
          ["matrix",   "Competency Matrix"],
          ["roster",   "Employee Roster"],
          ["catalog",  "Training Catalog"],
          ["upcoming", "Upcoming Training"],
        ].map(([k, l]) => (
          <button key={k} data-testid={`orgdev-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-full ${tab === k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/70"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="surface-card rounded-2xl p-10 text-center text-sm text-[#1a1a1a]/50">Loading Org-Dev…</div>
      ) : (
        <>
          {tab === "matrix"   && <MatrixTab matrix={matrix} competencies={competencies} canEdit={canEdit} reload={reload} />}
          {tab === "roster"   && <RosterTab employees={employees} canEdit={canEdit} reload={reload} matrix={matrix} competencies={competencies} />}
          {tab === "catalog"  && <CatalogTab competencies={competencies} canEdit={canEdit} reload={reload} documents={documents} employees={employees} />}
          {tab === "upcoming" && <UpcomingTab matrix={matrix} competencies={competencies} canEdit={canEdit} reload={reload} />}
        </>
      )}

      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
        <Sparkles className="w-3 h-3 inline mr-1" /> LIVE · Every signature is stamped into <b>document_changelog</b> for audit-ready traceability.
      </div>
    </div>
  );
}
